import { jwtDecode } from 'jwt-decode'

import { DEV_AUTH_ENABLED } from './runtime'

type RawClaims = {
  email?: string
  role?: string
  groups?: string[]
  source?: string
  ['cognito:groups']?: string[]
  ['cognito:username']?: string
}

type SessionTokens = {
  accessToken?: string | null
  idToken?: string | null
  refreshToken?: string | null
}

export type SessionInfo = {
  accessToken: string | null
  idToken: string | null
  refreshToken: string | null
  email: string
  role: string | null
  groups: string[]
  authSource: string | null
  isAdmin: boolean
  /** Owner or admin — can open audit log & CSV export */
  canViewAuditLogs: boolean
  isAuthenticated: boolean
}

function getLegacySession(token: string): SessionInfo | null {
  if (token === 'dev-admin-token' || token === 'beacon-dev-owner-token') {
    return {
      accessToken: token,
      idToken: localStorage.getItem('id_token'),
      refreshToken: localStorage.getItem('refresh_token'),
      email: 'owner@beacon.local',
      role: 'owner',
      groups: ['Admins', 'Users'],
      authSource: token.startsWith('beacon-') ? 'beacon-dev' : 'dev-legacy',
      isAdmin: true,
      canViewAuditLogs: true,
      isAuthenticated: true,
    }
  }

  if (token === 'dev-user-token') {
    return {
      accessToken: token,
      idToken: localStorage.getItem('id_token'),
      refreshToken: localStorage.getItem('refresh_token'),
      email: 'dev@beacon.local',
      role: 'team_member',
      groups: ['Users'],
      authSource: 'dev-legacy',
      isAdmin: false,
      canViewAuditLogs: false,
      isAuthenticated: true,
    }
  }

  if (token === 'beacon-dev-member-token') {
    return {
      accessToken: token,
      idToken: localStorage.getItem('id_token'),
      refreshToken: localStorage.getItem('refresh_token'),
      email: 'member@beacon.local',
      role: 'team_member',
      groups: ['Users'],
      authSource: 'beacon-dev',
      isAdmin: false,
      canViewAuditLogs: false,
      isAuthenticated: true,
    }
  }

  return null
}

function decodeClaims(token: string): RawClaims | null {
  const legacy = getLegacySession(token)
  if (legacy) {
    return {
      email: legacy.email,
      role: legacy.role ?? undefined,
      groups: legacy.groups,
      source: legacy.authSource ?? undefined,
    }
  }

  try {
    return jwtDecode<RawClaims>(token)
  } catch {
    return null
  }
}

function readStoredAccessToken(): string | null {
  return localStorage.getItem('access_token') || localStorage.getItem('auth_token')
}

export function getAccessToken(): string | null {
  const stored = readStoredAccessToken()
  if (stored) return stored
  if (DEV_AUTH_ENABLED) return 'dev-user-token'
  return null
}

export function getSessionInfo(): SessionInfo {
  const accessToken = getAccessToken()
  const idToken = localStorage.getItem('id_token')
  const refreshToken = localStorage.getItem('refresh_token')

  if (!accessToken) {
    return {
      accessToken: null,
      idToken,
      refreshToken,
      email: '',
      role: null,
      groups: [],
      authSource: null,
      isAdmin: false,
      canViewAuditLogs: false,
      isAuthenticated: false,
    }
  }

  const legacy = getLegacySession(accessToken)
  if (legacy) {
    return legacy
  }

  const claims = decodeClaims(accessToken)
  const groups = Array.isArray(claims?.groups)
    ? claims?.groups ?? []
    : Array.isArray(claims?.['cognito:groups'])
      ? claims?.['cognito:groups'] ?? []
      : []
  const role = claims?.role ?? null
  const email = claims?.email || claims?.['cognito:username'] || ''
  const isAdmin = groups.includes('Admins') || role === 'admin' || role === 'owner'
  // Audit logs are Owner / Admin only (Viewer/Auditor seats removed).
  const canViewAuditLogs = isAdmin

  return {
    accessToken,
    idToken,
    refreshToken,
    email,
    role,
    groups,
    authSource: claims?.source ?? null,
    isAdmin,
    canViewAuditLogs,
    isAuthenticated: true,
  }
}

export function getAuthHeader(): string {
  const token = getAccessToken()
  return token ? `Bearer ${token}` : ''
}

export function getRequiredAuthHeader(): string {
  const header = getAuthHeader()
  if (!header) {
    throw new Error('Not authenticated. Please log in again.')
  }
  return header
}

export function setAuthSession(tokens: SessionTokens): void {
  writeStorageToken('access_token', tokens.accessToken)
  writeStorageToken('id_token', tokens.idToken)
  writeStorageToken('refresh_token', tokens.refreshToken)
}

export function clearAuthSession(): void {
  localStorage.removeItem('access_token')
  localStorage.removeItem('id_token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('auth_token')
}

function writeStorageToken(key: string, value: string | null | undefined): void {
  if (value) {
    localStorage.setItem(key, value)
  } else {
    localStorage.removeItem(key)
  }
}
